import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Layers } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import SingleImageField from "../../components/common/SingleImageField";
import { Link } from "react-router-dom";

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const blank = {
  name: "",
  slug: "",
  description: "",
  gender: "unisex",
  allowed_category_slugs: [],
  is_active: true,
  measurement_required: true,
  sort_order: 0,
  icon_url: "",
};

export default function AdminClothingTypes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);
  const [catsStr, setCatsStr] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/stitching/clothing-types");
      setRows(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(blank);
    setCatsStr("");
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ ...blank, ...r });
    setCatsStr((r.allowed_category_slugs || []).join(", "));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const body = {
      ...form,
      slug: slugify(form.slug || form.name),
      sort_order: Number(form.sort_order) || 0,
      allowed_category_slugs: catsStr.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) await api.put(`/admin/stitching/clothing-types/${editingId}`, body);
      else await api.post("/admin/stitching/clothing-types", body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this clothing type and all its fields?")) return;
    try {
      await api.delete(`/admin/stitching/clothing-types/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="admin-clothing-types-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Stitching Master</div>
          <h1 className="font-display text-4xl">Clothing Types</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Define the clothing categories customers can measure and book appointments for.</p>
        </div>
        <button className="kb-btn-primary inline-flex items-center gap-2" onClick={openNew} data-testid="clothing-type-add-btn">
          <Plus size={14} /> New Clothing Type
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="clothing-types-loading">Loading…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="clothing-types-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">No clothing types yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr>
                <th className="p-3">#</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Gender</th>
                <th>Fields</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`clothing-type-row-${r.slug}`}>
                  <td className="p-3 font-mono">{r.sort_order}</td>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 font-mono text-xs">{r.slug}</td>
                  <td className="p-3 text-xs uppercase">{r.gender}</td>
                  <td className="p-3 text-xs">
                    <Link to={`/admin/stitching/${r.id}/fields`} className="underline hover:no-underline flex items-center gap-1" data-testid={`clothing-type-fields-${r.slug}`}>
                      <Layers size={12} /> {r.field_count ?? 0}
                    </Link>
                  </td>
                  <td className="p-3 text-xs uppercase">{r.is_active ? "yes" : "no"}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(r)} className="p-2 hover:bg-[hsl(var(--kb-surface-alt))]" data-testid={`clothing-type-edit-${r.slug}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(r.id)} className="p-2 hover:bg-[hsl(var(--kb-surface-alt))] text-red-700" data-testid={`clothing-type-del-${r.slug}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Clothing type"
        subtitle={editingId ? "Edit" : "New"}
        maxWidth="max-w-xl"
        testid="clothing-type-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)}>Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="clothing-type-save-btn">
              {editingId ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              className="kb-input"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
              data-testid="clothing-type-name"
            />
            <input
              className="kb-input"
              placeholder="Slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
              data-testid="clothing-type-slug"
            />
          </div>
          <textarea rows={2} className="kb-input" placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-3 gap-3">
            <select className="kb-input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="unisex">Unisex</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="kids">Kids</option>
            </select>
            <input type="number" className="kb-input" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
            </label>
          </div>
          <input className="kb-input" placeholder="Allowed category slugs (comma separated)" value={catsStr} onChange={(e) => setCatsStr(e.target.value)} data-testid="clothing-type-cats" />
          <SingleImageField
            label="Icon (optional)"
            value={form.icon_url}
            onChange={(key) => setForm({ ...form, icon_url: key || "" })}
            hint="Square icon, transparent background recommended"
            aspect="aspect-square"
            testid="clothing-type-icon"
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.measurement_required} onChange={(e) => setForm({ ...form, measurement_required: e.target.checked })} />
            Measurement required (else optional for these categories)
          </label>
        </div>
      </AdminDialog>
    </div>
  );
}
